import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Spin, Button, message, Modal, Progress, Tabs } from 'antd'
import {
  CheckCircleFilled,
  ArrowLeftOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  AppstoreOutlined,
  UserOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import styled, { keyframes, css } from 'styled-components'
import {
  getSetGroup,
  rejectSet,
  regenerateQuestion,
  assignStudentsToGroup,
  retryFailedGeneration,
  subscribeSSE,
  getBroadcast,
  pauseBroadcast,
  resumeBroadcast,
  endBroadcast,
  expandBroadcastPool,
  approveSet,
  updateBroadcastScope,
  updateBroadcastLabels,
  getBroadcastScopeHierarchy,
} from '../../services/assessmentSetGroupAPI'
import QuestionPreview from './QuestionPreview'
import AssignmentProgressModal from '../AssignmentActivity/AssignmentProgressModal'

/* ─── Animations ─── */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1.2); }
`

/* ─── Styled Components ─── */

const PageWrapper = styled.div`
  background: #FAFAFB;
  min-height: 100vh;
  animation: ${fadeIn} 0.3s ease-out;
`

const ReviewHeader = styled.div`
  background: white;
  padding: 0;
  border-bottom: 1px solid #EBECEE;
`

const HeaderInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px 28px;
`

const HeaderAccent = styled.div`
  width: 4px;
  min-height: 56px;
  border-radius: 2px;
  background: #3249D7;
  flex-shrink: 0;
  margin-top: 2px;
`

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const BackLink = styled.span`
  font-size: 13px;
  color: #637487;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 28px 0;
  transition: color 0.2s;
  &:hover { color: #3249D7; }
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #242E39;
  margin: 0 0 10px;
  line-height: 1.3;
`

const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #637487;
  background: #F0F2F4;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;

  .anticon {
    font-size: 11px;
    color: #8c99a8;
  }
`

const ProgressCard = styled.div`
  margin: 16px 28px 0;
  padding: 18px 22px;
  background: white;
  border: 1px solid #EBECEE;
  border-radius: 10px;
  box-shadow: 0px 1px 4px rgba(99, 116, 135, 0.08);
  animation: ${fadeIn} 0.3s ease-out 0.1s both;
`

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: ${props => props.$last ? '0' : '14px'};
`

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$color || '#242E39'};
  min-width: 140px;
  display: flex;
  align-items: center;
  gap: 6px;

  ${props => props.$pulsing && css`
    animation: ${pulse} 1.8s ease-in-out infinite;
  `}
`

const ProgressCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$color || '#637487'};
  min-width: 80px;
  text-align: right;
`

const SuccessBanner = styled.div`
  margin: 16px 28px 0;
  padding: 14px 20px;
  background: #DFF0EA;
  border: 1px solid #93D7AF;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${fadeIn} 0.4s ease-out;
`

const SuccessBannerIcon = styled.span`
  color: #4cb187;
  font-size: 18px;
  display: flex;
  align-items: center;
`

const SuccessBannerText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1a6b47;
`

const SuccessBannerSub = styled.span`
  font-size: 12px;
  color: #3a8c66;
  margin-left: 4px;
  font-weight: 400;
`

const TabsWrapper = styled.div`
  background: white;
  padding: 0 28px;
  border-bottom: 1px solid #EBECEE;
  margin-top: 16px;

  .ant-tabs {
    .ant-tabs-nav {
      margin-bottom: 0;

      &::before {
        border-bottom: none;
      }
    }

    .ant-tabs-tab {
      padding: 12px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #637487;
      border-radius: 8px 8px 0 0;
      transition: all 0.2s;

      &:hover {
        color: #3249D7;
      }

      &.ant-tabs-tab-active {
        .ant-tabs-tab-btn {
          color: #3249D7;
          font-weight: 600;
        }
      }
    }

    .ant-tabs-ink-bar {
      background: #3249D7;
      height: 3px;
      border-radius: 3px 3px 0 0;
    }
  }
`

const ContentArea = styled.div`
  padding: 20px 28px 0;
  background: #FAFAFB;
  animation: ${fadeIn} 0.2s ease-out;
`

const SetActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding: 14px 20px;
  background: white;
  border: 1px solid #EBECEE;
  border-radius: 10px;
  box-shadow: 0px 1px 4px rgba(99, 116, 135, 0.06);
`

const SetTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #242E39;
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ApprovedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: #DFF0EA;
  color: #1a6b47;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;

  .anticon {
    font-size: 14px;
    color: #4cb187;
  }
`

const FooterBar = styled.div`
  padding: 20px 28px;
  background: white;
  border-top: 1px solid #EBECEE;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
`

const AssignButton = styled(Button)`
  &&& {
    padding: 10px 40px;
    height: auto;
    font-size: 15px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    &:not(:disabled):hover {
      background: #444CE7;
      border-color: #444CE7;
    }
  }
`

const DisabledHint = styled.div`
  font-size: 12px;
  color: #637487;
  display: flex;
  align-items: center;
  gap: 5px;

  .anticon {
    font-size: 11px;
    color: #FFA642;
  }
`

const AssignedFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #DFF0EA;
  border-radius: 10px;
  border: 1px solid #93D7AF;
`

const AssignedFooterText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a6b47;
`

/* ─── Loading & Empty States ─── */

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #FAFAFB;
`

const LoadingSpinner = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 2px 10px rgba(99, 116, 135, 0.2);
  margin-bottom: 18px;

  .anticon {
    font-size: 24px;
    color: #3249D7;
  }
`

const LoadingText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #637487;
`

const GeneratingEmpty = styled.div`
  text-align: center;
  padding: 60px 28px;
  animation: ${fadeIn} 0.4s ease-out;
`

const GeneratingCard = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 40px 32px;
  background: white;
  border-radius: 10px;
  border: 1px solid #EBECEE;
  box-shadow: 0px 2px 10px rgba(99, 116, 135, 0.1);
`

const GeneratingDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 20px;
`

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #3249D7;
  animation: ${dotPulse} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
`

const GeneratingTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #242E39;
  margin-bottom: 8px;
`

const GeneratingSubtitle = styled.div`
  font-size: 13px;
  color: #637487;
  margin-bottom: 20px;
`

const SkeletonLine = styled.div`
  height: ${props => props.$h || '12px'};
  width: ${props => props.$w || '100%'};
  border-radius: 6px;
  background: linear-gradient(90deg, #F0F2F4 25%, #F8F9FA 50%, #F0F2F4 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  margin-bottom: ${props => props.$mb || '10px'};
`

const NotFoundWrapper = styled.div`
  padding: 24px 28px;
`

const NotFoundCard = styled.div`
  text-align: center;
  padding: 60px 24px;
  background: white;
  border-radius: 10px;
  border: 1px solid #EBECEE;
  margin-top: 16px;
`

const NotFoundText = styled.div`
  font-size: 15px;
  color: #637487;
  font-weight: 500;
`

/* ─── Broadcast Management Panel ─── */

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
`

const countUp = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`

const BroadcastPanel = styled.div`
  margin: 20px 28px 0;
  animation: ${slideDown} 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
`

const StatCard = styled.div`
  background: #fff;
  border: 1px solid #EBECEE;
  border-radius: 10px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideDown} 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${p => p.$delay || '0s'} both;

  &:hover {
    border-color: ${p => p.$accent || '#3249D7'};
    box-shadow: 0 2px 12px ${p => p.$accent || '#3249D7'}18;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${p => p.$accent || '#3249D7'};
    border-radius: 10px 10px 0 0;
  }
`

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #242E39;
  line-height: 1;
  margin-bottom: 6px;
  animation: ${countUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${p => p.$delay || '0s'} both;
`

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #637487;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const StatSub = styled.div`
  font-size: 11px;
  color: #8c99a8;
  margin-top: 4px;
`

const ControlsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  animation: ${slideDown} 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both;
`

const ControlCard = styled.div`
  flex: 1;
  background: #fff;
  border: 1px solid #EBECEE;
  border-radius: 10px;
  padding: 20px;
  transition: box-shadow 0.25s ease;

  &:hover {
    box-shadow: 0px 2px 10px rgba(99, 116, 135, 0.12);
  }
`

const ControlTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #242E39;
  margin-bottom: 6px;
`

const ControlDesc = styled.div`
  font-size: 12px;
  color: #637487;
  margin-bottom: 14px;
  line-height: 1.5;
`

const BroadcastStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 20px;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  transition: all 0.2s ease;
`

const ScopeTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  background: #F0F2F4;
  color: #637487;
  margin: 3px;
  transition: all 0.2s ease;
`

const SpecTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  background: #EAEDFB;
  color: #3249D7;
  margin: 3px;
`

const PoolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`

const PoolTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #F0F2F4;
  border-radius: 4px;
  overflow: hidden;
`

const PoolFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${p => p.$color || '#3249D7'};
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`

const PoolLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #637487;
  white-space: nowrap;
`

const BROADCAST_STATUS_CONFIG = {
  ACTIVE: { color: '#027A48', bg: '#DFF0EA', label: 'Active', dot: '#4cb187' },
  PAUSED: { color: '#B54708', bg: '#FFF7ED', label: 'Paused', dot: '#FFA642' },
  CLOSED: { color: '#637487', bg: '#F0F2F4', label: 'Ended', dot: '#8c99a8' },
  ASSIGNED: { color: '#027A48', bg: '#DFF0EA', label: 'Active', dot: '#4cb187' },
}

const LifecycleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  border: 1.5px solid;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;

  &:active { transform: scale(0.97); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const PauseBtn = styled(LifecycleBtn)`
  background: #FFF7ED;
  border-color: #FFA642;
  color: #B54708;
  &:hover:not(:disabled) { background: #FFF0DB; box-shadow: 0 2px 8px rgba(255, 166, 66, 0.25); }
`

const ResumeBtn = styled(LifecycleBtn)`
  background: #3249D7;
  border-color: #3249D7;
  color: #fff;
  &:hover:not(:disabled) { background: #444CE7; box-shadow: 0 2px 8px rgba(50, 73, 215, 0.3); }
`

const EndBtn = styled(LifecycleBtn)`
  background: #fff;
  border-color: #DC3232;
  color: #DC3232;
  &:hover:not(:disabled) { background: #FFF5F5; box-shadow: 0 2px 8px rgba(220, 50, 50, 0.2); }
`

const SetStatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
`

const BroadcastManagementPanel = ({ broadcastData, onAction, actionLoading, pendingSets, broadcastId, onScopeUpdated }) => {
  const [expandSets, setExpandSets] = useState(5)
  const [scopeModalVisible, setScopeModalVisible] = useState(false)

  if (!broadcastData) return null

  const { broadcast, stats, poolSize, nextSetIndex, quota } = broadcastData
  const bStatus = broadcast.status || 'ACTIVE'
  const statusConf = BROADCAST_STATUS_CONFIG[bStatus] || BROADCAST_STATUS_CONFIG.ACTIVE
  const isActive = bStatus === 'ACTIVE' || bStatus === 'ASSIGNED'
  const isPaused = bStatus === 'PAUSED'
  const isClosed = bStatus === 'CLOSED'

  const completionPct = stats.eligible > 0 ? Math.round((stats.submitted / stats.eligible) * 100) : 0
  const startedPct = stats.eligible > 0 ? Math.round((stats.started / stats.eligible) * 100) : 0
  const poolUsedPct = poolSize > 0 ? Math.round((nextSetIndex / poolSize) * 100) : 0

  return (
    <BroadcastPanel>
      {/* Pending sets banner */}
      {pendingSets > 0 && (
        <div style={{
          marginBottom: 16, padding: '10px 16px', background: '#FFF7ED', border: '1px solid #FFE0B2',
          borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8,
          animation: 'slideDown 0.3s ease',
        }}>
          <ClockCircleOutlined style={{ color: '#B54708', fontSize: 14 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#B54708' }}>
            {pendingSets} new set{pendingSets > 1 ? 's' : ''} pending review — approve in the set tabs below
          </span>
        </div>
      )}

      {/* Controls */}
      <ControlsRow>
        {/* Pool */}
        <ControlCard>
          <ControlTitle>Question Set Pool</ControlTitle>
          <ControlDesc>
            {nextSetIndex} students bound across {poolSize} sets
          </ControlDesc>
          <PoolBar>
            <PoolTrack>
              <PoolFill style={{ width: `${Math.min(poolUsedPct, 100)}%` }} $color={poolUsedPct >= 90 ? '#DC3232' : poolUsedPct >= 70 ? '#FFA642' : '#3249D7'} />
            </PoolTrack>
            <PoolLabel>{poolUsedPct}%</PoolLabel>
          </PoolBar>
          {!isClosed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <input
                type="number"
                min={1}
                max={20}
                value={expandSets}
                onChange={e => setExpandSets(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                style={{
                  width: 56, height: 34, borderRadius: 8, border: '1px solid #EBECEE',
                  textAlign: 'center', fontSize: 13, fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none',
                }}
              />
              <ResumeBtn style={{ padding: '6px 16px', fontSize: 12 }} disabled={actionLoading === 'expand'} onClick={() => onAction('expand', expandSets)}>
                {actionLoading === 'expand' ? <LoadingOutlined spin /> : null}
                Add Sets
              </ResumeBtn>
            </div>
          )}
        </ControlCard>

        {/* Scope & Labels */}
        <ControlCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <ControlTitle style={{ marginBottom: 0 }}>Scope & Labels</ControlTitle>
            {!isClosed && (
              <button
                onClick={() => setScopeModalVisible(true)}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: '1px solid #EBECEE', background: '#FAFAFB', color: '#3249D7',
                  fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.target.style.background = '#EAEDFB'; e.target.style.borderColor = '#3249D7' }}
                onMouseLeave={e => { e.target.style.background = '#FAFAFB'; e.target.style.borderColor = '#EBECEE' }}
              >
                Edit
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(broadcast.scopes || []).map((s, i) => {
              // Show labels that target THIS specific scope. NULL-target rows
              // are legacy broadcast-wide labels — fall back to streamIds for
              // back-compat if a legacy row also carries streamIds.
              const matchingSpecs = (broadcast.labelSpecialisations || [])
                .filter(l => {
                  if (l.targetDegreeId && l.targetStreamId) {
                    return l.targetDegreeId === s.targetDegreeId && l.targetStreamId === s.targetStreamId
                  }
                  // legacy NULL-target: broadcast-wide; show on every scope
                  return !l.streamIds || l.streamIds.length === 0 || l.streamIds.includes(s.targetStreamId)
                })
                .map(l => l.specialisationName)
                .filter(Boolean)
              const specPart = matchingSpecs.length > 0 ? ` - ${matchingSpecs.join(', ')}` : ''
              const scopeYears = Array.isArray(s.targetPassingYears) ? s.targetPassingYears : []
              const yearPart = scopeYears.length > 0 ? ` · ${scopeYears.join(', ')}` : ''
              return (
                <div key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '5px 12px', borderRadius: 8,
                  background: '#F0F2F4', border: '1px solid #EBECEE',
                  fontSize: 11, fontWeight: 600, color: '#242E39',
                }}>
                  {s.degreeName || 'Degree'} - {s.streamName || 'Dept'}{yearPart}{specPart}
                </div>
              )
            })}
            {(!broadcast.scopes || broadcast.scopes.length === 0) && (
              <ControlDesc>No scope configured</ControlDesc>
            )}
          </div>
        </ControlCard>
      </ControlsRow>

      <ScopeEditModal
        visible={scopeModalVisible}
        onClose={() => setScopeModalVisible(false)}
        broadcastId={broadcastId}
        currentScopes={broadcast.scopes}
        currentSpecs={broadcast.labelSpecialisations}
        onSaved={onScopeUpdated}
      />
    </BroadcastPanel>
  )
}

/* ─── Scope Edit Modal ─── */

const ScopeEditModal = ({ visible, onClose, broadcastId, currentScopes, currentSpecs, onSaved }) => {
  const [hierarchy, setHierarchy] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [scopes, setScopes] = useState([])
  // Add form state
  const [addDegree, setAddDegree] = useState(null)
  const [addStream, setAddStream] = useState(null)
  const [addSpecs, setAddSpecs] = useState([])
  const [addPassingYears, setAddPassingYears] = useState([])

  useEffect(() => {
    if (!visible || !broadcastId) return
    setLoading(true)
    getBroadcastScopeHierarchy(broadcastId)
      .then(res => {
        const h = res.data?.data || []
        setHierarchy(h)
        // Group incoming labels by their (targetDegreeId, targetStreamId).
        // Legacy rows (where target_* is null) get assigned to scopes by stream-spec membership as a one-time fallback.
        const perScope = {}
        const legacyFlatSpecs = []
        for (const l of currentSpecs || []) {
          if (l.targetDegreeId && l.targetStreamId) {
            const k = `${l.targetDegreeId}:${l.targetStreamId}`
            if (!perScope[k]) perScope[k] = []
            if (!perScope[k].includes(l.specialisationId)) perScope[k].push(l.specialisationId)
          } else {
            legacyFlatSpecs.push(l.specialisationId)
          }
        }
        const scopeList = (currentScopes || []).map(s => {
          const deg = h.find(d => d.degreeId === s.targetDegreeId)
          const str = deg?.streams?.find(st => st.streamId === s.targetStreamId)
          const k = `${s.targetDegreeId}:${s.targetStreamId}`
          let labelIds = perScope[k] || []
          if (labelIds.length === 0 && legacyFlatSpecs.length > 0) {
            const streamSpecIds = (str?.specialisations || []).map(sp => sp.specId)
            labelIds = streamSpecIds.filter(id => legacyFlatSpecs.includes(id))
          }
          return {
            targetDegreeId: s.targetDegreeId,
            targetStreamId: s.targetStreamId,
            degreeName: s.degreeName || deg?.degreeShort || deg?.degreeName || '?',
            streamName: s.streamName || str?.streamName || '?',
            labelSpecialisationIds: labelIds,
            targetPassingYears: Array.isArray(s.targetPassingYears) ? s.targetPassingYears : [],
          }
        })
        setScopes(scopeList)
      })
      .catch(() => message.error('Failed to load scope data'))
      .finally(() => setLoading(false))
  }, [visible, broadcastId])

  const addDeg = hierarchy.find(d => d.degreeId === addDegree)
  const addStreams = addDeg?.streams || []
  const addStr = addStreams.find(s => s.streamId === addStream)
  const addSpecsList = addStr?.specialisations || []

  const handleAdd = () => {
    if (!addDegree || !addStream) return
    const key = `${addDegree}:${addStream}`
    if (scopes.find(s => `${s.targetDegreeId}:${s.targetStreamId}` === key)) {
      message.warning('This scope already exists')
      return
    }
    const deg = hierarchy.find(d => d.degreeId === addDegree)
    const str = deg?.streams?.find(s => s.streamId === addStream)
    setScopes(prev => [...prev, {
      targetDegreeId: addDegree,
      targetStreamId: addStream,
      degreeName: deg?.degreeShort || deg?.degreeName,
      streamName: str?.streamName,
      labelSpecialisationIds: [...addSpecs],
      targetPassingYears: [...addPassingYears],
    }])
    setAddDegree(null)
    setAddStream(null)
    setAddSpecs([])
    setAddPassingYears([])
  }

  const handleRemoveScope = (scope) => {
    Modal.confirm({
      title: 'Remove Scope',
      content: `Remove "${scope.degreeName} - ${scope.streamName}" from this broadcast? Students in this department who haven't started will be hidden.`,
      okText: 'Remove',
      okType: 'danger',
      onOk: () => {
        setScopes(prev => prev.filter(s => !(s.targetDegreeId === scope.targetDegreeId && s.targetStreamId === scope.targetStreamId)))
      },
    })
  }

  const handleSave = async () => {
    if (scopes.length === 0) { message.warning('Add at least one scope'); return }
    setSaving(true)
    try {
      const res = await updateBroadcastScope(broadcastId, scopes.map(s => ({
        targetDegreeId: s.targetDegreeId,
        targetStreamId: s.targetStreamId,
        targetPassingYears: s.targetPassingYears || [],
      })))
      const perScopeLabels = scopes.map(s => ({
        targetDegreeId: s.targetDegreeId,
        targetStreamId: s.targetStreamId,
        specialisationIds: s.labelSpecialisationIds || [],
      }))
      await updateBroadcastLabels(broadcastId, perScopeLabels)
      const data = res.data?.data || {}
      const msg = data.emailsQueued > 0
        ? `Scope updated — ${data.newStudents || 0} new students enrolled, ${data.emailsQueued} emails queued`
        : 'Scope updated'
      message.success(msg)
      onSaved()
      onClose()
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to update scope')
    }
    setSaving(false)
  }

  // Build badge labels — only show specs explicitly added to THIS scope
  const getBadgeLabel = (scope) => {
    const deg = hierarchy.find(d => d.degreeId === scope.targetDegreeId)
    const str = deg?.streams?.find(s => s.streamId === scope.targetStreamId)
    const scopeSpecIds = scope.labelSpecialisationIds || []
    const streamSpecs = (str?.specialisations || []).filter(sp => scopeSpecIds.includes(sp.specId))
    const scopeYears = scope.targetPassingYears || []
    const yearPart = scopeYears.length > 0 ? ` · ${scopeYears.join(', ')}` : ''
    const specPart = streamSpecs.length > 0 ? ` - ${streamSpecs.map(s => s.specName).join(', ')}` : ''
    return `${scope.degreeName} - ${scope.streamName}${yearPart}${specPart}`
  }

  // Passing-year options come from the scope hierarchy (years that actually have
  // students for that degree+stream). Union in any already-selected years so a
  // previously-chosen year still renders even if it has no current students.
  const getYearOptions = (degreeId, streamId, selected = []) => {
    const deg = hierarchy.find(d => d.degreeId === degreeId)
    const str = deg?.streams?.find(s => s.streamId === streamId)
    const fromData = Array.isArray(str?.passingYears) ? str.passingYears : []
    return [...new Set([...fromData, ...(selected || [])])].sort((a, b) => b - a)
  }

  const togglePassingYear = (scopeIdx, year) => {
    setScopes(prev => prev.map((s, i) => {
      if (i !== scopeIdx) return s
      const years = s.targetPassingYears || []
      const next = years.includes(year) ? years.filter(y => y !== year) : [...years, year]
      return { ...s, targetPassingYears: next }
    }))
  }

  const selectStyle = {
    width: '100%', height: 38, borderRadius: 8, border: '1px solid #EBECEE', padding: '0 12px',
    fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: '#fff', color: '#242E39', outline: 'none', cursor: 'pointer',
    appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%23637487\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={null}
      footer={null}
      width={620}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
      style={{ top: 40 }}
    >
      <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {/* Header */}
        <div style={{ padding: '28px 32px 20px', borderBottom: '1px solid #F0F2F4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EAEDFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AppstoreOutlined style={{ fontSize: 16, color: '#3249D7' }} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#242E39' }}>Edit Scope & Labels</div>
          </div>
          <div style={{ fontSize: 13, color: '#637487', marginLeft: 42 }}>
            Adding a new scope enrolls eligible students and sends invite emails.
          </div>
        </div>

        <div style={{ padding: '20px 32px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
          ) : (
            <>
              {/* Current scopes */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#8c99a8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Active Scopes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {scopes.map((scope, i) => (
                    <div key={i} style={{
                      padding: '10px 14px', borderRadius: 10,
                      background: '#fff', border: '1px solid #EBECEE',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(99, 116, 135, 0.06)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4cb187', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#242E39' }}>{getBadgeLabel(scope)}</span>
                        </div>
                        {scopes.length > 1 && (
                          <button
                            onClick={() => handleRemoveScope(scope)}
                            style={{
                              width: 24, height: 24, borderRadius: 6, border: '1px solid transparent',
                              background: 'transparent', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', padding: 0,
                              color: '#8c99a8', transition: 'all 0.15s ease', flexShrink: 0,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#FFF0F0'; e.currentTarget.style.borderColor = '#FFCDD2'; e.currentTarget.style.color = '#DC3232' }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = '#8c99a8' }}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          </button>
                        )}
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#8c99a8', textTransform: 'uppercase', letterSpacing: 0.3, marginRight: 2 }}>Years:</span>
                        {(() => {
                          const opts = getYearOptions(scope.targetDegreeId, scope.targetStreamId, scope.targetPassingYears)
                          if (opts.length === 0) {
                            return <span style={{ fontSize: 10, color: '#8c99a8', fontStyle: 'italic' }}>no passing-year data (all years)</span>
                          }
                          return opts.map(yr => {
                            const active = (scope.targetPassingYears || []).includes(yr)
                            return (
                              <button key={yr} onClick={() => togglePassingYear(i, yr)}
                                style={{
                                  padding: '3px 10px', borderRadius: 100, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                                  border: active ? '1px solid #3249D7' : '1px solid #EBECEE',
                                  background: active ? '#EAEDFB' : '#fff', color: active ? '#3249D7' : '#8c99a8',
                                  fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s ease',
                                }}>{yr}</button>
                            )
                          })
                        })()}
                        {(scope.targetPassingYears || []).length === 0 && getYearOptions(scope.targetDegreeId, scope.targetStreamId).length > 0 && (
                          <span style={{ fontSize: 10, color: '#8c99a8', fontStyle: 'italic' }}>(all years)</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {scopes.length === 0 && (
                    <div style={{ padding: '16px 14px', borderRadius: 10, background: '#FAFAFB', border: '1px dashed #EBECEE', textAlign: 'center', fontSize: 13, color: '#8c99a8' }}>
                      No scopes — add one below
                    </div>
                  )}
                </div>
              </div>

              {/* Add new scope */}
              <div style={{ padding: '18px 18px 16px', background: '#FAFAFB', borderRadius: 12, border: '1px solid #EBECEE' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#3249D7', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M3 7H11" stroke="#3249D7" strokeWidth="2" strokeLinecap="round"/></svg>
                  Add New Scope
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: addSpecsList.length > 0 ? 14 : 14 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#8c99a8', marginBottom: 4 }}>Degree</div>
                    <select style={selectStyle} value={addDegree || ''} onChange={e => { setAddDegree(e.target.value); setAddStream(null); setAddSpecs([]) }}>
                      <option value="">Select degree...</option>
                      {hierarchy.map(d => <option key={d.degreeId} value={d.degreeId}>{d.degreeShort || d.degreeName}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#8c99a8', marginBottom: 4 }}>Department</div>
                    <select style={{ ...selectStyle, opacity: addStreams.length ? 1 : 0.5 }} value={addStream || ''} onChange={e => { setAddStream(e.target.value); setAddSpecs([]) }} disabled={!addStreams.length}>
                      <option value="">Select department...</option>
                      {addStreams.map(s => <option key={s.streamId} value={s.streamId}>{s.streamName}</option>)}
                    </select>
                  </div>
                </div>
                {addDegree && addStream && (
                  <div style={{ marginBottom: 14, padding: '12px 14px', background: '#fff', borderRadius: 8, border: '1px solid #F0F2F4' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#637487', marginBottom: 8 }}>
                      Passing Year <span style={{ fontWeight: 400, color: '#8c99a8' }}>— optional (leave empty for all years)</span>
                    </div>
                    {(() => {
                      const opts = getYearOptions(addDegree, addStream, addPassingYears)
                      if (opts.length === 0) {
                        return <div style={{ fontSize: 11, color: '#8c99a8', fontStyle: 'italic' }}>No passing-year data for this scope — all years will be included.</div>
                      }
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {opts.map(yr => {
                            const active = addPassingYears.includes(yr)
                            return (
                              <button key={yr} onClick={() => setAddPassingYears(prev => active ? prev.filter(x => x !== yr) : [...prev, yr])}
                                style={{
                                  padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                  border: active ? '1.5px solid #3249D7' : '1.5px solid #EBECEE',
                                  background: active ? '#3249D7' : '#fff', color: active ? '#fff' : '#637487',
                                  fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s ease',
                                }}>{yr}</button>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </div>
                )}
                {addSpecsList.length > 0 && (
                  <div style={{ marginBottom: 14, padding: '12px 14px', background: '#fff', borderRadius: 8, border: '1px solid #F0F2F4' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#637487', marginBottom: 8 }}>
                      Specialisations <span style={{ fontWeight: 400, color: '#8c99a8' }}>— emails sent only to selected (leave empty for all)</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {addSpecsList.map(sp => {
                        const active = addSpecs.includes(sp.specId)
                        return (
                          <button key={sp.specId} onClick={() => setAddSpecs(prev => active ? prev.filter(id => id !== sp.specId) : [...prev, sp.specId])}
                            style={{
                              padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              border: active ? '1.5px solid #3249D7' : '1.5px solid #EBECEE',
                              background: active ? '#3249D7' : '#fff', color: active ? '#fff' : '#637487',
                              fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                              transform: active ? 'scale(1.02)' : 'scale(1)',
                            }}>
                            {sp.specName}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
                <button
                  onClick={handleAdd}
                  disabled={!addDegree || !addStream}
                  style={{
                    padding: '8px 20px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: 'none',
                    background: addDegree && addStream ? 'linear-gradient(135deg, #3249D7, #444CE7)' : '#F0F2F4',
                    color: addDegree && addStream ? '#fff' : '#8c99a8',
                    fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.2s ease',
                    opacity: !addDegree || !addStream ? 0.6 : 1,
                    boxShadow: addDegree && addStream ? '0 2px 8px rgba(50, 73, 215, 0.25)' : 'none',
                  }}>
                  + Add to Scope
                </button>
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, paddingTop: 18, borderTop: '1px solid #F0F2F4' }}>
            <button onClick={onClose} style={{
              padding: '9px 22px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: '1px solid #EBECEE', background: '#fff', color: '#637487',
              fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s ease',
            }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || scopes.length === 0}
              style={{
                padding: '9px 28px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none', background: saving || scopes.length === 0 ? '#F0F2F4' : 'linear-gradient(135deg, #3249D7, #444CE7)',
                color: saving || scopes.length === 0 ? '#8c99a8' : '#fff',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                transition: 'all 0.2s ease',
                boxShadow: saving || scopes.length === 0 ? 'none' : '0 2px 8px rgba(50, 73, 215, 0.25)',
              }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

/* ─── Status Badge ─── */

const StatusBadge = ({ status }) => {
  const config = {
    PENDING: { color: '#B54708', bg: '#FFF7ED', border: '#FFE0B2', label: 'Pending Review', icon: <ClockCircleOutlined /> },
    APPROVED: { color: '#027A48', bg: '#DFF0EA', border: '#93D7AF', label: 'Approved', icon: <CheckCircleFilled /> },
    REJECTED: { color: '#DC3232', bg: '#FFF0F0', border: '#FFCDD2', label: 'Rejected', icon: <ExclamationCircleOutlined /> },
  }
  const c = config[status] || config.PENDING
  return (
    <span style={{
      fontSize: 12,
      fontWeight: 600,
      padding: '4px 12px',
      borderRadius: 20,
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
    }}>
      {c.icon}
      {c.label}
    </span>
  )
}

/* ─── Tab Label Component ─── */

const TabLabel = ({ set, index, isBroadcastAssigned }) => {
  const isPending = set.approvalStatus === 'PENDING'
  const isApproved = set.approvalStatus === 'APPROVED'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {isBroadcastAssigned && (
        <SetStatusDot style={{
          background: isPending ? '#FFA642' : isApproved ? '#4cb187' : '#3249D7',
          boxShadow: isPending ? '0 0 0 2px rgba(255, 166, 66, 0.2)' : 'none',
        }} />
      )}
      Set {set.setNumber || index + 1}
      {isBroadcastAssigned && isPending && (
        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', background: '#FFF7ED', color: '#B54708', borderRadius: 4, border: '1px solid #FFE0B2' }}>NEW</span>
      )}
    </span>
  )
}

/* ─── Main Component ─── */

const AssessmentSetReview = () => {
  const { groupId } = useParams()
  const navigate = useNavigate()
  const [group, setGroup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeSetIndex, setActiveSetIndex] = useState(0)
  const [progressJobId, setProgressJobId] = useState(null) // live assignment popup
  const [actionLoading, setActionLoading] = useState(false)
  const [regeneratingQuestions, setRegeneratingQuestions] = useState(new Set())
  const [broadcastData, setBroadcastData] = useState(null)
  const [broadcastActionLoading, setBroadcastActionLoading] = useState(null)

  const fetchGroup = useCallback(async () => {
    try {
      const res = await getSetGroup(groupId)
      setGroup(res.data?.data || null)
    } catch (err) {
      console.error('Failed to fetch group:', err)
      message.error('Failed to load assessment group')
    }
    setLoading(false)
  }, [groupId])

  useEffect(() => {
    fetchGroup()
  }, [fetchGroup])

  // SSE subscription for live progress (generation + pool expansion)
  useEffect(() => {
    const needsSSE = group && (
      group.status === 'GENERATING' ||
      (group.isBroadcast && group.status === 'ASSIGNED')
    )
    if (!needsSSE) return

    const es = subscribeSSE(`assessment-generation:${groupId}`)

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'SET_GENERATED' || data.type === 'ALL_SETS_GENERATED') {
          fetchGroup()
        }
        if (data.type === 'POOL_EXPANDED') {
          message.success('New sets generated and auto-approved')
          fetchGroup()
          if (group.broadcastId) {
            getBroadcast(group.broadcastId).then(res => setBroadcastData(res.data?.data || null)).catch(() => {})
          }
        }
        if (data.type === 'GENERATION_FAILED') {
          fetchGroup()
          message.error('Some sets failed to generate')
        }
      } catch (e) {}
    }

    return () => es.close()
  }, [group?.status, group?.isBroadcast, groupId, fetchGroup])


  // Fetch broadcast dashboard data when group is a broadcast and assigned
  useEffect(() => {
    if (!group?.isBroadcast || !group?.broadcastId) return
    if (group.status !== 'ASSIGNED') return
    const fetchBroadcastData = async () => {
      try {
        const res = await getBroadcast(group.broadcastId)
        setBroadcastData(res.data?.data || null)
      } catch (e) {
        console.error('Failed to fetch broadcast data:', e)
      }
    }
    fetchBroadcastData()
  }, [group?.isBroadcast, group?.broadcastId, group?.status])

  const handleBroadcastAction = async (action, payload) => {
    if (!group?.broadcastId) return
    const bid = group.broadcastId

    if (action === 'end') {
      Modal.confirm({
        title: 'End Broadcast',
        content: 'This will permanently close the broadcast. Students will no longer be able to start the assessment. This cannot be undone.',
        okText: 'End Broadcast',
        okType: 'danger',
        onOk: async () => {
          setBroadcastActionLoading('end')
          try {
            await endBroadcast(bid)
            message.success('Broadcast ended')
            const res = await getBroadcast(bid)
            setBroadcastData(res.data?.data || null)
            fetchGroup()
          } catch (e) {
            message.error(e.response?.data?.message || 'Failed to end broadcast')
          }
          setBroadcastActionLoading(null)
        },
      })
      return
    }

    setBroadcastActionLoading(action)
    try {
      if (action === 'pause') {
        await pauseBroadcast(bid)
        message.success('Broadcast paused')
      } else if (action === 'resume') {
        await resumeBroadcast(bid)
        message.success('Broadcast resumed')
      } else if (action === 'expand') {
        const prevTotal = group.totalSets
        await expandBroadcastPool(bid, payload)
        message.info(`Generating ${payload} new sets...`)
        const res2 = await getBroadcast(bid)
        setBroadcastData(res2.data?.data || null)
        await fetchGroup()
        setActiveSetIndex(prevTotal)
        setBroadcastActionLoading(null)
        return
      }
      const res = await getBroadcast(bid)
      setBroadcastData(res.data?.data || null)
      fetchGroup()
    } catch (e) {
      message.error(e.response?.data?.message || `Failed to ${action} broadcast`)
    }
    setBroadcastActionLoading(null)
  }

  const handleApproveSet = async (setId) => {
    setActionLoading(true)
    try {
      await approveSet(groupId, setId)
      message.success('Set activated — now in the round-robin pool')
      fetchGroup()
      if (group?.broadcastId) {
        getBroadcast(group.broadcastId).then(res => setBroadcastData(res.data?.data || null)).catch(() => {})
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to activate set')
    }
    setActionLoading(false)
  }

  const handleRegenerate = async (setId) => {
    Modal.confirm({
      title: 'Regenerate Set',
      content: 'This will delete the current set and generate a new one. Continue?',
      okText: 'Regenerate',
      okType: 'danger',
      onOk: async () => {
        setActionLoading(true)
        try {
          await rejectSet(groupId, setId)
          message.success('Regeneration queued.')
          fetchGroup()
        } catch (err) {
          message.error(err.response?.data?.message || 'Failed to regenerate set')
        }
        setActionLoading(false)
      },
    })
  }

  const handleRegenerateQuestion = async (setId, questionId) => {
    setRegeneratingQuestions(prev => new Set(prev).add(questionId))
    try {
      await regenerateQuestion(groupId, setId, questionId)
      message.success('Question regenerated')
      fetchGroup()
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to regenerate question')
    }
    setRegeneratingQuestions(prev => {
      const next = new Set(prev)
      next.delete(questionId)
      return next
    })
  }

  const handleAssign = async () => {
    const isBroadcast = group.isBroadcast
    Modal.confirm({
      title: isBroadcast ? 'Activate Broadcast' : 'Assign to Students',
      content: isBroadcast
        ? `This will activate the broadcast assessment, auto-approve all pending sets, create memberships for eligible students, and send invite emails. Continue?`
        : `This will assign ${group.totalStudents} students across ${group.totalSets} sets. Students will receive assessment invites. Continue?`,
      okText: isBroadcast ? 'Activate' : 'Assign',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true)
        try {
          const res = await assignStudentsToGroup(groupId)
          const data = res.data?.data
          if (data?.async && data?.jobId) {
            // Queue-based assignment → open the live progress popup.
            setProgressJobId(data.jobId)
          } else if (data?.broadcastActivated) {
            message.success(`Broadcast activated — ${data.totalAssigned || 0} students enrolled, ${data.emailsQueued || 0} invite emails queued`)
            fetchGroup()
          } else {
            message.success(`Successfully assigned ${data?.totalAssigned || group.totalStudents || 0} students`)
            fetchGroup()
          }
        } catch (err) {
          message.error(err.response?.data?.message || 'Failed to assign students')
        }
        setActionLoading(false)
      },
    })
  }

  const handleRetryGeneration = async () => {
    setActionLoading(true)
    try {
      await retryFailedGeneration(groupId)
      message.success('Retrying failed sets...')
      fetchGroup()
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to retry generation')
    }
    setActionLoading(false)
  }

  /* ─── Loading State ─── */

  if (loading) {
    return (
      <LoadingWrapper>
        <LoadingSpinner>
          <LoadingOutlined spin />
        </LoadingSpinner>
        <LoadingText>Loading assessment review...</LoadingText>
      </LoadingWrapper>
    )
  }

  /* ─── Not Found State ─── */

  if (!group) {
    return (
      <PageWrapper>
        <NotFoundWrapper>
          <BackLink onClick={() => navigate('/assessment/reviews')}>
            <ArrowLeftOutlined /> Back to Reviews
          </BackLink>
          <NotFoundCard>
            <ExclamationCircleOutlined style={{ fontSize: 36, color: '#EBECEE', marginBottom: 12 }} />
            <NotFoundText>Assessment group not found</NotFoundText>
          </NotFoundCard>
        </NotFoundWrapper>
      </PageWrapper>
    )
  }

  const payload = group.generationPayload || {}
  const sets = group.sets || []
  const isReadyToAssign = group.status === 'REVIEW_PENDING' || group.status === 'ALL_APPROVED'
  const isAssigned = group.status === 'ASSIGNED'
  const isGenerating = group.status === 'GENERATING'
  const isFailed = group.status === 'GENERATION_FAILED'

  // Build full tab list including generating placeholders
  const generatingSetsCount = Math.max(0, group.totalSets - sets.length)
  const allSetTabs = [
    ...sets.map(s => ({ ...s, _generating: false })),
    ...Array.from({ length: generatingSetsCount }, (_, i) => ({
      _generating: true,
      setNumber: sets.length + i + 1,
      approvalStatus: 'GENERATING',
    })),
  ]
  const currentSet = allSetTabs[activeSetIndex]

  const genPercent = group.totalSets > 0
    ? Math.round((group.generatedSets / group.totalSets) * 100)
    : 0

  return (
    <PageWrapper>
      {/* ─── Assigned Banner ─── */}
      {isAssigned && !group.isBroadcast && (
        <SuccessBanner>
          <SuccessBannerIcon><CheckCircleFilled /></SuccessBannerIcon>
          <div>
            <SuccessBannerText>
              Assessment assigned successfully
            </SuccessBannerText>
            <SuccessBannerSub>
              &mdash; {group.totalStudents} students across {group.totalSets} sets
            </SuccessBannerSub>
          </div>
        </SuccessBanner>
      )}

      {/* ─── Header ─── */}
      <ReviewHeader>
        <BackLink onClick={() => navigate('/assessment/reviews')}>
          <ArrowLeftOutlined /> Back to Reviews
        </BackLink>
        <HeaderInner>
          <HeaderAccent />
          <HeaderContent>
            <Title>
              {payload.roleName || 'Assessment'}
              {group.isBroadcast && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', marginLeft: 10, background: '#EAEDFB', color: '#3249D7', borderRadius: 4, verticalAlign: 'middle' }}>BROADCAST</span>
              )}
            </Title>
            <MetaChips>
              {payload.seniority && (
                <MetaChip>
                  <UserOutlined />
                  {payload.seniority}
                </MetaChip>
              )}
              {!group.isBroadcast && (
                <MetaChip>
                  <TeamOutlined />
                  {group.totalStudents} students
                </MetaChip>
              )}
              <MetaChip>
                <AppstoreOutlined />
                {group.totalSets} sets
              </MetaChip>
              <MetaChip>
                Max {group.maxStudentsPerSet} per set
              </MetaChip>
            </MetaChips>
          </HeaderContent>
        </HeaderInner>
      </ReviewHeader>

      {/* ─── Progress Section ─── */}
      <ProgressCard>
        {/* Generation Progress */}
        <ProgressRow $last={!isGenerating}>
          <ProgressLabel $color="#3249D7" $pulsing={isGenerating}>
            {isGenerating && <LoadingOutlined spin style={{ fontSize: 12 }} />}
            Generation
          </ProgressLabel>
          <Progress
            percent={isGenerating || isFailed ? genPercent : 100}
            size="small"
            strokeColor={isFailed ? '#DC3232' : '#3249D7'}
            trailColor="#F0F2F4"
            style={{ flex: 1, maxWidth: 300 }}
            showInfo={false}
          />
          <ProgressCount $color={isFailed ? '#DC3232' : '#3249D7'}>
            {isGenerating || isFailed
              ? `${group.generatedSets} / ${group.totalSets}`
              : `${group.totalSets} / ${group.totalSets}`
            }
          </ProgressCount>
        </ProgressRow>

        {/* Failed Banner */}
        {isFailed && (
          <div style={{
            marginTop: 14,
            padding: '10px 16px',
            background: '#FFF0F0',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#DC3232', fontSize: 14 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#a31515' }}>
                Some sets failed to generate ({group.generatedSets} / {group.totalSets} completed)
              </span>
            </div>
            <Button
              type="primary"
              danger
              size="small"
              loading={actionLoading}
              onClick={handleRetryGeneration}
              icon={<ReloadOutlined />}
            >
              Retry Failed Sets
            </Button>
          </div>
        )}

        {/* Ready to Assign Banner */}
        {isReadyToAssign && !isAssigned && (
          <div style={{
            marginTop: 14,
            padding: '10px 16px',
            background: '#DFF0EA',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <CheckCircleFilled style={{ color: '#4cb187', fontSize: 14 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a6b47' }}>
              All sets generated — ready to assign
            </span>
          </div>
        )}
      </ProgressCard>

      {/* ─── Broadcast Management Panel ─── */}
      {group.isBroadcast && isAssigned && broadcastData && (
        <BroadcastManagementPanel
          broadcastData={broadcastData}
          onAction={handleBroadcastAction}
          actionLoading={broadcastActionLoading}
          pendingSets={sets.filter(s => s.approvalStatus === 'PENDING').length}
          broadcastId={group.broadcastId}
          onScopeUpdated={() => {
            getBroadcast(group.broadcastId).then(res => setBroadcastData(res.data?.data || null)).catch(() => {})
            fetchGroup()
          }}
        />
      )}

      {/* ─── Set Tabs ─── */}
      {allSetTabs.length > 0 && (
        <TabsWrapper>
          <Tabs
            activeKey={String(activeSetIndex)}
            onChange={(key) => setActiveSetIndex(parseInt(key, 10))}
            tabBarStyle={{ marginBottom: 0 }}
            items={allSetTabs.map((set, index) => ({
              key: String(index),
              label: set._generating ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#3249D7' }}>
                  <LoadingOutlined spin style={{ fontSize: 11 }} />
                  Set {set.setNumber}
                </span>
              ) : (
                <TabLabel set={set} index={index} isBroadcastAssigned={group.isBroadcast && isAssigned} />
              ),
            }))}
          />
        </TabsWrapper>
      )}

      {/* ─── Set Content ─── */}
      <ContentArea>
        {currentSet?._generating && (
          <GeneratingEmpty>
            <GeneratingCard>
              <GeneratingDots>
                <Dot $delay="0s" />
                <Dot $delay="0.2s" />
                <Dot $delay="0.4s" />
              </GeneratingDots>
              <GeneratingTitle>Generating Set {currentSet.setNumber}</GeneratingTitle>
              <GeneratingSubtitle>
                AI is creating a new question set for pool expansion. This may take a minute.
              </GeneratingSubtitle>
              <Progress
                percent={genPercent}
                strokeColor="#3249D7"
                trailColor="#F0F2F4"
                style={{ maxWidth: 280, margin: '0 auto 20px' }}
                format={pct => `${pct}%`}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 20px' }}>
                <SkeletonLine $h="14px" $w="85%" />
                <SkeletonLine $h="10px" $w="100%" />
                <SkeletonLine $h="10px" $w="70%" />
                <SkeletonLine $h="14px" $w="60%" $mb="0" />
              </div>
            </GeneratingCard>
          </GeneratingEmpty>
        )}
        {currentSet && !currentSet._generating && (
          <>
            <SetActionBar>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <SetTitle>Set {currentSet.setNumber || activeSetIndex + 1}</SetTitle>
                {group.isBroadcast && isAssigned && currentSet.approvalStatus === 'PENDING' && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', background: '#FFF7ED', color: '#B54708', borderRadius: 6, border: '1px solid #FFE0B2' }}>
                    Pending Review
                  </span>
                )}
                {group.isBroadcast && isAssigned && currentSet.approvalStatus === 'APPROVED' && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', background: '#DFF0EA', color: '#027A48', borderRadius: 6, border: '1px solid #93D7AF', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircleFilled style={{ fontSize: 10 }} /> In Pool
                  </span>
                )}
              </div>
              {(!isAssigned || (group.isBroadcast && isAssigned && currentSet.approvalStatus === 'PENDING')) && (
                <ActionButtons>
                  {group.isBroadcast && isAssigned && currentSet.approvalStatus === 'PENDING' && (
                    <Button
                      type="primary"
                      loading={actionLoading}
                      onClick={() => handleApproveSet(currentSet.id)}
                      icon={<CheckCircleFilled />}
                      style={{ borderRadius: 8, fontWeight: 600, height: 36, background: '#4cb187', borderColor: '#4cb187' }}
                    >
                      Activate Set
                    </Button>
                  )}
                  <Button
                    danger
                    ghost
                    loading={actionLoading}
                    onClick={() => handleRegenerate(currentSet.id)}
                    icon={<ReloadOutlined />}
                    style={{ borderRadius: 8, fontWeight: 500, height: 36 }}
                  >
                    Regenerate Set
                  </Button>
                </ActionButtons>
              )}
            </SetActionBar>

            <QuestionPreview
              set={currentSet}
              onRegenerateQuestion={(questionId) => handleRegenerateQuestion(currentSet.id, questionId)}
              regeneratingQuestions={regeneratingQuestions}
              isReadOnly={isAssigned && !(group.isBroadcast && currentSet.approvalStatus === 'PENDING')}
            />
          </>
        )}

        {/* ─── Generating Empty State ─── */}
        {isGenerating && sets.length === 0 && (
          <GeneratingEmpty>
            <GeneratingCard>
              <GeneratingDots>
                <Dot $delay="0s" />
                <Dot $delay="0.2s" />
                <Dot $delay="0.4s" />
              </GeneratingDots>
              <GeneratingTitle>Generating Assessment Sets</GeneratingTitle>
              <GeneratingSubtitle>
                AI is creating {group.totalSets} unique question sets for this assessment.
                This may take a minute.
              </GeneratingSubtitle>
              <Progress
                percent={genPercent}
                strokeColor="#3249D7"
                trailColor="#F0F2F4"
                style={{ maxWidth: 280, margin: '0 auto 20px' }}
                format={pct => `${pct}%`}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 20px' }}>
                <SkeletonLine $h="14px" $w="85%" />
                <SkeletonLine $h="10px" $w="100%" />
                <SkeletonLine $h="10px" $w="70%" />
                <SkeletonLine $h="14px" $w="60%" $mb="0" />
              </div>
            </GeneratingCard>
          </GeneratingEmpty>
        )}
      </ContentArea>

      {/* ─── Footer ─── */}
      <FooterBar>
        {isAssigned ? (
          <AssignedFooter>
            <CheckCircleFilled style={{ fontSize: 18, color: '#4cb187' }} />
            <AssignedFooterText>
              {group.isBroadcast
                ? `Broadcast is live — ${broadcastData?.stats?.eligible || 0} eligible, ${broadcastData?.stats?.started || 0} started, ${broadcastData?.stats?.submitted || 0} submitted`
                : `Assigned to ${group.totalStudents} students across ${group.totalSets} sets`
              }
            </AssignedFooterText>
          </AssignedFooter>
        ) : (
          <>
            <AssignButton
              type="primary"
              size="large"
              disabled={!isReadyToAssign}
              loading={actionLoading}
              onClick={handleAssign}
              icon={<TeamOutlined />}
            >
              {group.isBroadcast ? 'Activate Broadcast' : `Assign to ${group.totalStudents} Students`}
            </AssignButton>
            {!isReadyToAssign && !isGenerating && (
              <DisabledHint>
                <ExclamationCircleOutlined />
                Wait for all sets to be generated
              </DisabledHint>
            )}
          </>
        )}
      </FooterBar>

      <AssignmentProgressModal
        open={!!progressJobId}
        jobId={progressJobId}
        total={group?.totalStudents || 0}
        assessmentName={group?.generationPayload?.roleName || group?.name}
        onContinue={() => { setProgressJobId(null); fetchGroup() }}
        onClose={() => { setProgressJobId(null); fetchGroup() }}
        onViewActivity={() => navigate(`/assessment/reviews?expandGroup=${groupId}&entity=${group?.entityType || ''}`)}
      />
    </PageWrapper>
  )
}

class AssessmentSetReviewErrorBoundary extends React.Component {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(err) { console.error('AssessmentSetReview error:', err) }
  render() {
    if (this.state.hasError) {
      return (
        <PageWrapper>
          <NotFoundWrapper>
            <NotFoundCard>
              <ExclamationCircleOutlined style={{ fontSize: 36, color: '#EBECEE', marginBottom: 12 }} />
              <NotFoundText>Something went wrong loading this page. Please go back and try again.</NotFoundText>
              <Button style={{ marginTop: 16 }} onClick={() => window.location.href = '/assessment/reviews'}>
                Back to Reviews
              </Button>
            </NotFoundCard>
          </NotFoundWrapper>
        </PageWrapper>
      )
    }
    return this.props.children
  }
}

const AssessmentSetReviewWithBoundary = (props) => (
  <AssessmentSetReviewErrorBoundary>
    <AssessmentSetReview {...props} />
  </AssessmentSetReviewErrorBoundary>
)

export default AssessmentSetReviewWithBoundary
